import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Lock,
  LogOut,
  Loader2,
  MessageCircle,
  Users,
  CalendarDays,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { BRAND } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mv_admin_token";

const inputCls =
  "w-full rounded-lg border border-slate-600/50 bg-navy-950/70 px-4 py-3 text-sm font-light text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-gold-400/70";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

const fmtBookingDate = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const waDigits = (phone) => phone.replace(/\D/g, "");

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const loadData = useCallback(
    async (t) => {
      setRefreshing(true);
      try {
        const headers = { Authorization: `Bearer ${t}` };
        const [l, b] = await Promise.all([
          axios.get(`${API}/admin/leads`, { headers }),
          axios.get(`${API}/admin/bookings`, { headers }),
        ]);
        setLeads(l.data.leads);
        setBookings(b.data.bookings);
      } catch (e) {
        if (e?.response?.status === 401) logout();
      } finally {
        setRefreshing(false);
      }
    },
    [logout]
  );

  const remove = async (kind, id, name) => {
    if (!window.confirm(`Excluir "${name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/admin/${kind}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadData(token);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => loadData(token))
      .catch(() => logout())
      .finally(() => setChecking(false));
  }, [token, loadData, logout]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Falha no login. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950">
        <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-navy-950 px-5">
        <motion.form
          data-testid="admin-login-form"
          onSubmit={submitLogin}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-sm rounded-2xl border border-gold-400/25 bg-navy-800/70 p-8 backdrop-blur-sm"
        >
          <div className="mb-8 text-center">
            <img
              src={BRAND.logo}
              alt="Melo Vistos"
              className="mx-auto h-14 w-14 rounded-lg border border-gold-400/40 object-cover"
            />
            <p className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] text-gold-400">
              ÁREA RESTRITA
            </p>
            <h1 className="font-serif-display mt-2 text-2xl text-slate-100">
              Painel Melo Vistos
            </h1>
          </div>
          <label className="mb-2 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            E-MAIL
          </label>
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@email.com"
            className={inputCls}
          />
          <label className="mb-2 mt-5 block font-mono-tech text-[10px] tracking-[0.25em] text-slate-400">
            SENHA
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            className={inputCls}
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gold-400 px-6 py-3.5 text-sm font-semibold text-navy-950 transition-colors hover:bg-gold-200 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Entrar
          </button>
          <Link
            to="/"
            className="mt-5 block text-center text-xs font-light text-slate-500 transition-colors hover:text-gold-300"
          >
            ← Voltar ao site
          </Link>
        </motion.form>
      </div>
    );
  }

  const upcoming = bookings.filter((b) => `${b.date}T${b.time}` >= new Date().toISOString().slice(0, 16));

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy-950">
      <header className="border-b border-gold-400/15 bg-navy-900/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Melo Vistos"
              className="h-10 w-10 rounded-md border border-gold-400/40 object-cover"
            />
            <div>
              <p className="font-mono-tech text-xs font-semibold tracking-[0.28em] text-slate-100">
                PAINEL <span className="text-gold-400">MELO VISTOS</span>
              </p>
              <p className="font-mono-tech text-[9px] tracking-widest text-slate-500">
                LEADS & AGENDAMENTOS
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => loadData(token)}
              className="flex items-center gap-2 rounded-full border border-slate-600/60 px-4 py-2 text-xs text-slate-300 transition-colors hover:border-gold-400/60 hover:text-gold-200"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Atualizar
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-slate-600/60 px-4 py-2 text-xs text-slate-300 transition-colors hover:border-red-400/60 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="mb-8 grid grid-cols-3 gap-4">
          {[
            { icon: Users, label: "Leads recebidos", value: leads.length, testid: "admin-stat-leads" },
            { icon: CalendarDays, label: "Agendamentos", value: bookings.length, testid: "admin-stat-bookings" },
            { icon: CalendarDays, label: "Próximas consultorias", value: upcoming.length, testid: "admin-stat-upcoming" },
          ].map((s) => (
            <div
              key={s.testid}
              data-testid={s.testid}
              className="rounded-xl border border-gold-400/20 bg-navy-800/70 p-5"
            >
              <s.icon className="h-5 w-5 text-gold-400" />
              <p className="font-serif-display mt-3 text-3xl text-slate-100">{s.value}</p>
              <p className="mt-1 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-slate-500">
                {s.label}
              </p>
            </div>
          ))}
        </div>

        <div className="mb-6 flex gap-2">
          <button
            data-testid="admin-tab-leads"
            onClick={() => setTab("leads")}
            className={`rounded-full border px-5 py-2 text-sm transition-colors ${
              tab === "leads"
                ? "border-gold-400 bg-gold-400/15 text-gold-200"
                : "border-slate-700 text-slate-400 hover:text-gold-300"
            }`}
          >
            Leads ({leads.length})
          </button>
          <button
            data-testid="admin-tab-bookings"
            onClick={() => setTab("bookings")}
            className={`rounded-full border px-5 py-2 text-sm transition-colors ${
              tab === "bookings"
                ? "border-gold-400 bg-gold-400/15 text-gold-200"
                : "border-slate-700 text-slate-400 hover:text-gold-300"
            }`}
          >
            Agendamentos ({bookings.length})
          </button>
        </div>

        {tab === "leads" && (
          <div data-testid="admin-leads-list" className="space-y-3">
            {leads.length === 0 && (
              <p className="py-10 text-center text-sm font-light text-slate-500">
                Nenhum lead recebido ainda.
              </p>
            )}
            {leads.map((l) => (
              <div
                key={l.id}
                data-testid="admin-lead-row"
                className="flex flex-col gap-4 rounded-xl border border-gold-400/15 bg-navy-800/70 p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="text-base text-slate-100">{l.name}</p>
                  <p className="mt-1 text-xs font-light text-slate-400">
                    {l.email} • {l.phone}
                  </p>
                  <p className="mt-1 font-mono-tech text-[10px] tracking-wider text-gold-300">
                    {l.destination.toUpperCase()}
                  </p>
                  {l.message && (
                    <p className="mt-2 max-w-xl text-xs font-light italic text-slate-500">
                      “{l.message}”
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  <span className="font-mono-tech text-[10px] text-slate-500">
                    {fmtDate(l.created_at)}
                  </span>
                  <div className="flex items-center gap-2">
                    <a
                      data-testid={`admin-lead-whatsapp-${l.id}`}
                      href={`https://wa.me/55${waDigits(l.phone)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-xs text-emerald-300 transition-colors hover:bg-emerald-500 hover:text-navy-950"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      Chamar no WhatsApp
                    </a>
                    <button
                      data-testid={`admin-lead-delete-${l.id}`}
                      onClick={() => remove("leads", l.id, l.name)}
                      className="flex items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 p-2 text-red-400 transition-colors hover:bg-red-500 hover:text-white"
                      aria-label={`Excluir ${l.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings-list" className="space-y-3">
            {bookings.length === 0 && (
              <p className="py-10 text-center text-sm font-light text-slate-500">
                Nenhuma consultoria agendada ainda.
              </p>
            )}
            {bookings.map((b) => (
              <div
                key={b.id}
                data-testid="admin-booking-row"
                className="flex flex-col gap-4 rounded-xl border border-gold-400/15 bg-navy-800/70 p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-5">
                  <div className="flex h-14 w-16 shrink-0 flex-col items-center justify-center rounded-lg border border-gold-400/40 bg-gold-400/10">
                    <span className="font-serif-display text-lg leading-none text-gold-200">
                      {b.time}
                    </span>
                    <span className="mt-1 font-mono-tech text-[9px] text-gold-400">
                      {fmtBookingDate(b.date)}
                    </span>
                  </div>
                  <div>
                    <p className="text-base text-slate-100">{b.name}</p>
                    <p className="mt-1 text-xs font-light text-slate-400">
                      {b.email} • {b.phone}
                    </p>
                    {b.destination && (
                      <p className="mt-1 font-mono-tech text-[10px] tracking-wider text-gold-300">
                        {b.destination.toUpperCase()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-fit shrink-0 items-center gap-2">
                  <a
                    data-testid={`admin-booking-whatsapp-${b.id}`}
                    href={`https://wa.me/55${waDigits(b.phone)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-xs text-emerald-300 transition-colors hover:bg-emerald-500 hover:text-navy-950"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    Chamar no WhatsApp
                  </a>
                  <button
                    data-testid={`admin-booking-delete-${b.id}`}
                    onClick={() => remove("bookings", b.id, b.name)}
                    className="flex items-center justify-center rounded-full border border-red-500/30 bg-red-500/10 p-2 text-red-400 transition-colors hover:bg-red-500 hover:text-white"
                    aria-label={`Excluir ${b.name}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
